import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Logo from './Logo';
import { BRAND_COLOR } from '../config/brand';

const PRIMARY = '#3498db';
const NAVY = '#0F172A';

const NAV_ITEMS = [
  { id: 'home',             label: 'Home',             href: '/#home',            desktopOnly: false },
  { id: 'how-it-works',    label: 'How It Works',      href: '/#how-it-works',    desktopOnly: false },
  { id: 'testimonials',    label: 'Testimonials',      href: '/#testimonials',    desktopOnly: false },
  { id: 'faq',             label: 'FAQ',               href: '/#faq',             desktopOnly: false },
  { id: 'tracking-portal', label: 'Tracking Portal',   href: '/trackingPortal',   desktopOnly: true  },
];

const Navbar: React.FC = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const isLight = scrolled || !isHome;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Section IDs in DOM order: home → how-it-works → testimonials → cta → faq
    // (cta is excluded since it's not a nav item; last match in iteration wins)
    const sectionIds = ['home', 'how-it-works', 'testimonials', 'faq'];

    const handleScroll = () => {
      const scrollY = window.scrollY;
      let current = 'home';

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        // offsetTop relative to document; subtract 80px for sticky navbar height
        if (scrollY >= el.offsetTop - 80) {
          current = id;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once after a short delay to allow React Router to render sections
    const t = setTimeout(handleScroll, 100);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(t);
    };
  }, []);

  const isMobile = windowWidth < 940;

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      background: isLight
        ? 'rgba(255,255,255,0.97)'
        : (isHome && !isMobile)
          ? 'rgba(8, 20, 52, 0.82)'
          : 'linear-gradient(135deg, #0F172A 0%, #1e3a5f 60%, #1a6fa8 100%)',
      backdropFilter: (isHome && !isMobile) ? 'blur(20px)' : 'none',
      WebkitBackdropFilter: (isHome && !isMobile) ? 'blur(20px)' : 'none',
      boxShadow: isLight
        ? '0 1px 0 rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.08)'
        : '0 1px 0 rgba(52,152,219,0.25), 0 4px 28px rgba(0,0,0,0.30)',
      borderBottom: isLight
        ? '1px solid rgba(52,152,219,0.10)'
        : '1px solid rgba(52,152,219,0.28)',
      transition: 'background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
    }}>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '0 28px',
        height: 68,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>

        {/* Logo */}
        <a href="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
          <Logo
            size={isMobile ? 44 : 52}
            showName={true}
            bgColor={BRAND_COLOR}
            borderColor={'rgba(255,255,255,0.12)'}
            nameColor={isLight ? NAVY : '#ffffff'}
            imageScale={0.9}
            innerPadding={0.06}
            boxShadow={'0 6px 20px rgba(0,0,0,0.24)'}
          />
        </a>

        {/* Desktop nav */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {NAV_ITEMS.map(item => {
              const isActive = activeSection === item.id;
              const isHov = hovered === item.id;
              return (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={item.id === 'home'
                    ? (e: React.MouseEvent) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }
                    : undefined}
                  onMouseEnter={() => setHovered(item.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    borderRadius: 8,
                    fontSize: 14.5,
                    fontWeight: isActive ? 700 : 500,
                    letterSpacing: '0.01em',
                    textDecoration: 'none',
                    color: isLight
                      ? (isActive ? PRIMARY : (isHov ? PRIMARY : '#334155'))
                      : (isActive ? '#fff' : (isHov ? '#fff' : 'rgba(255,255,255,0.78)')),
                    background: isHov
                      ? (isLight ? 'rgba(52,152,219,0.07)' : 'rgba(255,255,255,0.08)')
                      : 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {item.label}
                  {isActive && (
                    <span style={{
                      position: 'absolute',
                      bottom: 4,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 18,
                      height: 2.5,
                      borderRadius: 2,
                      background: isLight ? PRIMARY : '#fff',
                    }} />
                  )}
                </a>
              );
            })}
          </div>
        )}

        {/* Desktop CTA */}
        {!isMobile && (
          <a
            href="/#cta"
            onMouseEnter={() => setHovered('cta')}
            onMouseLeave={() => setHovered(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 22px',
              borderRadius: 10,
              fontSize: 14.5,
              fontWeight: 700,
              textDecoration: 'none',
              letterSpacing: '0.01em',
              background: isLight
                ? (hovered === 'cta' ? '#2980b9' : PRIMARY)
                : (hovered === 'cta' ? 'rgba(255,255,255,0.95)' : '#fff'),
              color: isLight ? '#fff' : PRIMARY,
              boxShadow: isLight
                ? '0 4px 16px rgba(52,152,219,0.35)'
                : '0 4px 16px rgba(0,0,0,0.18)',
              transform: hovered === 'cta' ? 'translateY(-2px)' : 'none',
              transition: 'all 0.22s ease',
            }}
          >
            Get Started
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <button
            onClick={() => setIsMenuOpen(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              {isMenuOpen
                ? <path d="M6 6l12 12M6 18L18 6" stroke={isLight ? NAVY : '#fff'} strokeWidth="2.2" strokeLinecap="round" />
                : <path d="M4 6h16M4 12h16M4 18h16" stroke={isLight ? NAVY : '#fff'} strokeWidth="2.2" strokeLinecap="round" />
              }
            </svg>
          </button>
        )}
      </div>

      {/* Mobile menu */}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
          borderTop: '1px solid rgba(52,152,219,0.10)',
          padding: '12px 20px 20px',
        }}>
          {NAV_ITEMS.filter(item => !item.desktopOnly).map(item => (
            <a
              key={item.id}
              href={item.href}
              onClick={item.id === 'home'
                ? (e: React.MouseEvent) => { e.preventDefault(); setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }
                : () => setIsMenuOpen(false)}
              style={{
                display: 'block',
                padding: '13px 12px',
                fontSize: 15,
                fontWeight: activeSection === item.id ? 700 : 500,
                color: activeSection === item.id ? PRIMARY : '#334155',
                textDecoration: 'none',
                borderBottom: '1px solid #f1f5f9',
                borderRadius: 6,
              }}
            >
              {item.label}
            </a>
          ))}
          <a
            href="/#cta"
            onClick={() => setIsMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 14,
              padding: '13px',
              background: PRIMARY,
              color: '#fff',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 15,
              textDecoration: 'none',
              boxShadow: '0 4px 16px rgba(52,152,219,0.35)',
            }}
          >
            Get Started
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
